import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getDoc, setDoc } from "@firebase/firestore";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";
import { Feedback } from "@allaria-tech/matter/Feedback";
import Layout from "../Layout/Layout";
import { getCompanyData } from "../../utils/companies";
import { getEmailValidationRef, loadFavicon } from "../../utils";
import { authToken } from "../../connectors/connectors";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { updateToken } from "../../reducers/authReducer";
import { onUpdateDraftUUID } from "../../reducers/onboardingDataReducer";
import { getDraftUUIDFromToken } from "../../app/utils";
import useWindowWidth from "../../hooks/useWindowWidth";

import styles from "./EmailVerificationPage.module.scss";
import { EmailValidationData } from "../../steps/physical/emailValidationStep";
import { getUrlTemplate } from "../../hooks/useTemplateInterpreter";
import { clearPersistedOnboarding } from "../../app/store";

type VerificationStatus = "LOADING" | "SUCCESS" | "INVALID";

const REDIRECT_SECONDS = 5;

const EmailVerificationPage = () => {
  const { verificationBody, countdownText } = styles;

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const id = searchParams.get("id"); // SignatureRequestId
  const email = (searchParams.get("email") || "").replace(" ", "+");
  const statusParam =
    (searchParams.get("status") as VerificationStatus) || null;
  const isRedirect = token && id && email;

  const [status, setStatus] = useState<VerificationStatus>(
    statusParam || "LOADING",
  );
  const [emailValidation, setEmailValidation] =
    useState<EmailValidationData | null>(null);

  const { favicon } = getCompanyData();
  const isMobile = useWindowWidth().width < 768;

  const dispatch = useAppDispatch();
  const resumeToken = useAppSelector((s) => s.authentication.token);
  const resumeDraftUUID = useAppSelector((s) => s.onboardingData.draftUUID);

  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  const emailValidationRef = id ? getEmailValidationRef(id) : null;

  const fetchEmailValidation = async () => {
    if (!emailValidationRef) return;
    const snap = await getDoc(emailValidationRef).catch(() => null);
    if (snap?.exists()) {
      //Merged: a read that lands after our own `verified` write only carries
      //that field, and would drop the onboardingType the redirect needs
      setEmailValidation((prev) => ({
        ...prev,
        ...(snap.data() as EmailValidationData),
      }));
    }
  };

  const handleContinue = async () => {
    if (!emailValidation) return;

    const basePath = getUrlTemplate({
      onboardingType: emailValidation.onboardingType,
    });

    // Hard navigation: the onboarding route boots into its own redux-persist
    // bucket (keyed by pathname), so the in-memory state written on this page
    // can't be read there. Carry what the resume needs in the URL; everything
    // else is refetched from the draft during the template's onLoad. `token` is
    // the irreducible value — getDraft authenticates from it and draftUUID is
    // decoded from it. `cuit` needs `email` alongside it to be persisted on the
    // destination (see the `email && cuit` guard in TemplateInterpreter).
    const params = new URLSearchParams({ preload: "true" });
    if (resumeToken) params.set("token", resumeToken);
    if (resumeDraftUUID) params.set("uuid", resumeDraftUUID);
    // if (email) params.set("email", email);
    // if (cuit) params.set("cuit", cuit);
    // if (isJuridical && businessCuit) params.set("businessCuit", businessCuit);

    await clearPersistedOnboarding(basePath);
    window.location.assign(`${basePath}?${params.toString()}`);
  };

  const verifyToken = async () => {
    if (!token || !id || !email) {
      setStatus("INVALID");
      return;
    }
    try {
      if (!emailValidationRef) return;

      const authResponse = await authToken({
        email,
        signatureRequestId: Number(id),
        token,
      });

      const draftUUID = getDraftUUIDFromToken({
        token: authResponse.data.token,
      });

      dispatch(updateToken(authResponse.data.token));
      dispatch(onUpdateDraftUUID(draftUUID));
      //Read before writing: on a fresh browser the first read is slow, and
      //once the write below is pending it only returns `verified`
      await fetchEmailValidation();
      // await getDraft({ draftUUID }).catch(() => null);
      await setDoc(emailValidationRef, { verified: true }, { merge: true });
      setStatus("SUCCESS");
    } catch (error) {
      console.log(error);
      setStatus("INVALID");
    }
  };

  useEffect(() => {
    if (favicon) loadFavicon(favicon);
  }, [favicon]);

  useEffect(() => {
    if (statusParam || !email) return;
    verifyToken();
  }, [statusParam, token, id, email]);

  useEffect(() => {
    if (!email) return;
    fetchEmailValidation();
  }, [email]);

  useEffect(() => {
    if (status !== "SUCCESS" || !isRedirect || !email) return;
    if (countdown <= 0) {
      handleContinue();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, isRedirect, countdown]);

  const continueAction = { text: "Continuar", onClick: handleContinue };

  const successFeedbackProps = {
    type: "SUCCESS" as const,
    title: "¡Verificaste tu email!",
    description: isRedirect
      ? undefined
      : "Podés cerrar esta ventana y seguir con la apertura de tu cuenta.",
    primaryAction: isRedirect ? continueAction : undefined,
    children: isRedirect ? (
      <p className={countdownText}>
        Te vamos a redireccionar para que continúes con la apertura de tu cuenta
        en{" "}
        <strong>
          {countdown} {countdown === 1 ? "segundo" : "segundos"}.
        </strong>
      </p>
    ) : undefined,
  };

  const invalidFeedbackProps = {
    type: "ERROR" as const,
    title: "El enlace no es válido",
    description:
      "Puede que haya expirado o ya fue usado. Cerrá esta ventana y solicitá uno nuevo para continuar.",
  };

  return (
    <Layout centeredLogo hideStepper>
      <div className={verificationBody}>
        {status === "LOADING" && <LoadingCircle />}

        {status === "SUCCESS" &&
          (isMobile ? (
            <Feedback isOpen {...successFeedbackProps} />
          ) : (
            <Feedback inline {...successFeedbackProps} />
          ))}

        {status === "INVALID" &&
          (isMobile ? (
            <Feedback isOpen {...invalidFeedbackProps} />
          ) : (
            <Feedback inline {...invalidFeedbackProps} />
          ))}
      </div>
    </Layout>
  );
};

export default EmailVerificationPage;
